import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { notifications } from "@mantine/notifications";

import InputField from "../atom/InputField";
import SelectField from "../atom/SelectField";
import FileUploadField from "../molecule/FileUploadField";
import { Tabler } from "../molecule/Icons";
import { Button } from "../../../dashboard/components/atom/Button";
import { API_BASE_URL } from "../../../config/api";

const MAX_ITEMS = 5;
const ALLOWED_DOC_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ALLOWED_EXTS = [".pdf", ".doc", ".docx"];

// Robust doc-type check (MIME OR extension)
const isAllowedDoc = (file) => {
  if (!file) return false;
  const typeOk = !!file.type && ALLOWED_DOC_TYPES.includes(file.type);
  const name = (file.name || "").toLowerCase();
  const extOk = ALLOWED_EXTS.some((ext) => name.endsWith(ext));
  return typeOk || extOk;
};

const authHeaders = () => {
  // public endpoint – no auth headers
  return {};
};

// Lightweight stacked modal
const Modal = ({ open, title, children, onClose, footer, allowClose = true }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div data-compact className="w-full max-w-3xl mt-10 bg-white rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="text-base font-semibold">{title}</h3>
          {allowClose && (
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <Tabler.IconX size={18} />
            </button>
          )}
        </div>
        <div className="mt-3 space-y-3">{children}</div>
        {footer && <div className="flex justify-end mt-4">{footer}</div>}
      </div>
    </div>
  );
};

// Centered, square, professional success dialog
const SuccessDialog = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-[360px] h-[360px] flex flex-col items-center justify-center relative">
        {/* Close (optional) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-gray-100"
          aria-label="Close"
        >
          <Tabler.IconX size={18} />
        </button>

        {/* Icon */}
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 success-burst">
          <Tabler.IconCheck size={28} />
        </div>

        {/* Title only (no redirect text) */}
        <h3 className="text-xl font-semibold text-gray-900 text-center">
          Application Submitted
        </h3>
      </div>

      <style>{`
        .success-burst {
          background: radial-gradient(closest-side, rgba(255, 204, 0, 0.25), rgba(255, 204, 0, 0) 70%);
          border: 2px solid #FFCC00;
          animation: popIn 240ms ease-out;
        }
        @keyframes popIn {
          0% { transform: scale(0.85); opacity: 0.4; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// helpers
const todayYMD = () => new Date().toISOString().slice(0, 10);
const isFuture = (d) => d && d > todayYMD();
const validDateOrder = (start, end) => !start || !end || start <= end;

const ApplicationForm = () => {
  const { jobid } = useParams();
  const [loading, setLoading] = useState(false);
  const [cvFile, setCvFile] = useState(null);

  // Privacy consent
  const [consent, setConsent] = useState(false);

  // Lists (what we submit)
  const [educationList, setEducationList] = useState([]);
  const [workList, setWorkList] = useState([]);
  const [certList, setCertList] = useState([]);

  // Success dialog (post-submit)
  const [showSuccess, setShowSuccess] = useState(false);

  // Modals visibility
  const [showEduModal, setShowEduModal] = useState(false);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);

  // Drafts for the modal — fully controlled
  const [edu, setEdu] = useState({
    institution: "",
    degree: "",
    start: "",
    end: "",
    present: false,
    doc: null,
  });
  const [work, setWork] = useState({
    company: "",
    role: "",
    start: "",
    end: "",
    present: false,
    doc: null,
  });
  const [cert, setCert] = useState({
    name: "",
    issuer: "",
    year: "",
    doc: null,
  });

  // Inline error state for field-level messages
  const [errors, setErrors] = useState({
    phone: "",
    privacy: "",
    cvFile: "",
    mtnValue: "",
  });

  // === Add Handlers ===
  const addEducation = () => {
    if (educationList.length >= MAX_ITEMS) {
      notifications.show({
        title: "Limit reached",
        message: `You can add up to ${MAX_ITEMS} education entries.`,
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }
    const institution = (edu.institution || "").trim();
    const degree = (edu.degree || "").trim();

    if (!institution || !degree) {
      notifications.show({
        title: "Missing required fields",
        message: "Institution and Degree/Program are required.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    // date validations: no future, start <= end (unless Present)
    if (isFuture(edu.start) || (!edu.present && isFuture(edu.end))) {
      notifications.show({
        title: "Invalid dates",
        message: "Start/End dates cannot be in the future.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }
    if (!edu.present && !validDateOrder(edu.start, edu.end)) {
      notifications.show({
        title: "Invalid date range",
        message: "Start date cannot be greater than end date.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    // DOCUMENT NOW MANDATORY for Education
    if (!edu.doc) {
      notifications.show({
        title: "Document required",
        message: "Please attach your Education document (PDF or Word).",
        color: "red",
        icon: <Tabler.IconAlertTriangle size={18} />,
      });
      return;
    }
    if (!isAllowedDoc(edu.doc)) {
      notifications.show({
        title: "Invalid document",
        message: "Only PDF and Word documents are allowed.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    setEducationList((prev) => [...prev, { ...edu, institution, degree }]);
    setEdu({ institution: "", degree: "", start: "", end: "", present: false, doc: null });
    setShowEduModal(false);
  };

  const addWork = () => {
    if (workList.length >= MAX_ITEMS) {
      notifications.show({
        title: "Limit reached",
        message: `You can add up to ${MAX_ITEMS} work experiences.`,
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }
    const company = (work.company || "").trim();
    const role = (work.role || "").trim();

    if (!company || !role) {
      notifications.show({
        title: "Missing required fields",
        message: "Company and Role/Title are required.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    // date validations
    if (isFuture(work.start) || (!work.present && isFuture(work.end))) {
      notifications.show({
        title: "Invalid dates",
        message: "Start/End dates cannot be in the future.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }
    if (!work.present && !validDateOrder(work.start, work.end)) {
      notifications.show({
        title: "Invalid date range",
        message: "Start date cannot be greater than end date.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    // Work doc remains optional
    if (work.doc && !isAllowedDoc(work.doc)) {
      notifications.show({
        title: "Invalid document",
        message: "Only PDF and Word documents are allowed.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    setWorkList((prev) => [...prev, { ...work, company, role }]);
    setWork({ company: "", role: "", start: "", end: "", present: false, doc: null });
    setShowWorkModal(false);
  };

  const addCert = () => {
    if (certList.length >= MAX_ITEMS) {
      notifications.show({
        title: "Limit reached",
        message: `You can add up to ${MAX_ITEMS} certifications.`,
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }
    const name = (cert.name || "").trim();
    const issuer = (cert.issuer || "").trim();

    if (!name || !issuer) {
      notifications.show({
        title: "Missing required fields",
        message: "Certification Name and Issuer are required.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    // DOCUMENT NOW MANDATORY for Certification
    if (!cert.doc) {
      notifications.show({
        title: "Document required",
        message: "Please attach your Certification document (PDF or Word).",
        color: "red",
        icon: <Tabler.IconAlertTriangle size={18} />,
      });
      return;
    }
    if (!isAllowedDoc(cert.doc)) {
      notifications.show({
        title: "Invalid document",
        message: "Only PDF and Word documents are allowed.",
        color: "red",
        icon: <Tabler.IconX size={18} />,
      });
      return;
    }

    setCertList((prev) => [...prev, { ...cert, name, issuer }]);
    setCert({ name: "", issuer: "", year: "", doc: null });
    setShowCertModal(false);
  };

  // Remove from lists
  const removeEdu = (i) => setEducationList((prev) => prev.filter((_, idx) => idx !== i));
  const removeWork = (i) => setWorkList((prev) => prev.filter((_, idx) => idx !== i));
  const removeCert = (i) => setCertList((prev) => prev.filter((_, idx) => idx !== i));

  // sanitize phone input on the fly (digits only, max 10)
  const enforcePhoneDigits = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    e.target.value = digits;
  };

  // Inline + popup validation on submit
  const validateBeforeSubmit = (form) => {
    const nextErrors = {};

    // privacy
    if (!consent) {
      nextErrors.privacy = "Please agree to the Privacy notice.";
    }

    // phone
    const phone = (form.phone.value || "").trim();
    if (!/^\d{10}$/.test(phone)) {
      nextErrors.phone = "Phone must be exactly 10 digits.";
    }

    // CV
    if (!cvFile) {
      nextErrors.cvFile = "Please upload your CV (PDF or Word).";
    } else if (!isAllowedDoc(cvFile)) {
      nextErrors.cvFile = "CV must be a PDF or Word document.";
    }

    // MTN Value
    if (!form.mtnValue.value) {
      nextErrors.mtnValue = "Please select the MTN Value that resonates with you.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      notifications.show({
        title: "Please fix the highlighted fields",
        message: "Some required fields are missing or invalid.",
        color: "red",
        icon: <Tabler.IconAlertTriangle size={18} />,
      });
      return false;
    }
    return true;
  };

  // === Submit ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    if (!validateBeforeSubmit(form)) return;

    const phone = (form.phone.value || "").trim();
    const formData = new FormData();

    // basic fields
    formData.append("firstName", form.firstName.value.trim());
    formData.append("lastName", form.lastName.value.trim());
    formData.append("email", form.email.value.trim());
    formData.append("phone", phone);
    formData.append("gender", form.gender.value);
    formData.append("previousRole", form.previousRole.value.trim());
    formData.append("jobId", jobid);

    // lists -> string fields + files (end can be “Present”)
    educationList.forEach((eRow, i) => {
      const endText = eRow.present ? "Present" : eRow.end || "";
      formData.append(
        `education[${i}]`,
        `${eRow.degree} - ${eRow.institution}${
          eRow.start || endText ? ` (${[eRow.start, endText].filter(Boolean).join(" - ")})` : ""
        }`
      );
      if (eRow.doc) formData.append(`educationDocs[${i}]`, eRow.doc);
    });
    workList.forEach((wRow, i) => {
      const endText = wRow.present ? "Present" : wRow.end || "";
      formData.append(
        `work[${i}]`,
        `${wRow.role} - ${wRow.company}${
          wRow.start || endText ? ` (${[wRow.start, endText].filter(Boolean).join(" - ")})` : ""
        }`
      );
      if (wRow.doc) formData.append(`workDocs[${i}]`, wRow.doc);
    });
    certList.forEach((cRow, i) => {
      formData.append(
        `cert[${i}]`,
        `${cRow.name} - ${cRow.issuer}${cRow.year ? ` (${cRow.year})` : ""}`
      );
      if (cRow.doc) formData.append(`certDocs[${i}]`, cRow.doc);
    });

    // JSON fallbacks for robustness
    formData.append(
      "educationJson",
      JSON.stringify(
        educationList.map(({ institution, degree, start, end, present }) => ({
          institution,
          degree,
          start,
          end: present ? "Present" : end,
          present,
        }))
      )
    );
    formData.append(
      "workJson",
      JSON.stringify(
        workList.map(({ company, role, start, end, present }) => ({
          company,
          role,
          start,
          end: present ? "Present" : end,
          present,
        }))
      )
    );
    formData.append(
      "certJson",
      JSON.stringify(
        certList.map(({ name, issuer, year }) => ({
          name,
          issuer,
          year,
        }))
      )
    );

    // CV (already validated)
    formData.append("cvFile", cvFile);

    // MTN value LAST
    formData.append("mtnValue", form.mtnValue.value);

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/applications`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      // success dialog then redirect to MTN careers (no on-screen redirect text)
      setShowSuccess(true);
      setTimeout(() => {
        window.location.href = "https://www.mtn.co.rw/careers/";
      }, 2500);

      // reset local UI state
      form.reset();
      setConsent(false);
      setCvFile(null);
      setEducationList([]);
      setWorkList([]);
      setCertList([]);
      setErrors({ phone: "", privacy: "", cvFile: "", mtnValue: "" });
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data ||
        err?.message;

      notifications.show({
        title: "Submission Failed",
        message: `Status: ${status ?? "N/A"} — ${
          typeof serverMsg === "string" ? serverMsg : JSON.stringify(serverMsg)
        }`,
        color: "red",
        icon: <Tabler.IconX size={18} />,
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const maxDate = useMemo(() => todayYMD(), []);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <main className="flex-grow p-4 overflow-y-auto">
        <form onSubmit={handleSubmit} data-compact className="max-w-3xl pb-16 mx-auto space-y-3">
          <h2 className="text-lg font-bold">Apply for a Position</h2>

          {/* Basics */}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <InputField
              label={
                <span>
                  First Name <span className="text-red-600">*</span>
                </span>
              }
              name="firstName"
              required
            />
            <InputField
              label={
                <span>
                  Last Name <span className="text-red-600">*</span>
                </span>
              }
              name="lastName"
              required
            />
            <InputField
              label={
                <span>
                  Email <span className="text-red-600">*</span>
                </span>
              }
              name="email"
              type="email"
              required
            />
            <div>
              <InputField
                label={
                  <span>
                    Phone <span className="text-red-600">*</span>
                  </span>
                }
                name="phone"
                type="tel"
                required
                maxLength={10}
                pattern="[0-9]{10}"
                inputMode="numeric"
                onInput={enforcePhoneDigits}
                placeholder="07XXXXXXXX"
              />
              {errors.phone ? (
                <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
              ) : null}
            </div>
            <SelectField
              label="Gender"
              name="gender"
              options={[
                { value: "", label: "Select" },
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ]}
            />
          </div>

          <InputField label="Previous Role" name="previousRole" />

          {/* Education Section */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowEduModal(true)}
              className="flex items-center justify-between w-full px-3 py-2 bg-white border rounded-md hover:bg-gray-50"
            >
              <span className="text-sm font-medium">
                Education Background ({educationList.length}/{MAX_ITEMS})
              </span>
              <Tabler.IconEdit size={16} />
            </button>
            {educationList.map((e, i) => (
              <div
                key={`edu-${i}`}
                className="flex justify-between items-center px-3 py-2 bg-yellow-50 border rounded-md text-sm"
              >
                <span>
                  <strong>Education Background {i + 1}:</strong> {e.degree} — {e.institution}{" "}
                  {e.start || e.end || e.present
                    ? `(${[e.start, e.present ? "Present" : e.end].filter(Boolean).join(" - ")})`
                    : ""}
                </span>
                <button
                  onClick={() => removeEdu(i)}
                  type="button"
                  className="text-red-600 hover:text-red-800"
                  aria-label={`Remove Education Background ${i + 1}`}
                >
                  <Tabler.IconTrash size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Work Section */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowWorkModal(true)}
              className="flex items-center justify-between w-full px-3 py-2 bg-white border rounded-md hover:bg-gray-50"
            >
              <span className="text-sm font-medium">
                Work Experience ({workList.length}/{MAX_ITEMS})
              </span>
              <Tabler.IconEdit size={16} />
            </button>
            {workList.map((w, i) => (
              <div
                key={`work-${i}`}
                className="flex justify-between items-center px-3 py-2 bg-yellow-50 border rounded-md text-sm"
              >
                <span>
                  <strong>Work Experience {i + 1}:</strong> {w.role} — {w.company}{" "}
                  {w.start || w.end || w.present
                    ? `(${[w.start, w.present ? "Present" : w.end].filter(Boolean).join(" - ")})`
                    : ""}
                </span>
                <button
                  onClick={() => removeWork(i)}
                  type="button"
                  className="text-red-600 hover:text-red-800"
                  aria-label={`Remove Work Experience ${i + 1}`}
                >
                  <Tabler.IconTrash size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Certifications Section */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowCertModal(true)}
              className="flex items-center justify-between w-full px-3 py-2 bg-white border rounded-md hover:bg-gray-50"
            >
              <span className="text-sm font-medium">
                Certifications ({certList.length}/{MAX_ITEMS})
              </span>
              <Tabler.IconEdit size={16} />
            </button>
            {certList.map((c, i) => (
              <div
                key={`cert-${i}`}
                className="flex justify-between items-center px-3 py-2 bg-yellow-50 border rounded-md text-sm"
              >
                <span>
                  <strong>Certification {i + 1}:</strong> {c.name} — {c.issuer}
                </span>
                <button
                  onClick={() => removeCert(i)}
                  type="button"
                  className="text-red-600 hover:text-red-800"
                  aria-label={`Remove Certification ${i + 1}`}
                >
                  <Tabler.IconTrash size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* CV */}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div>
              <FileUploadField
                label={
                  <span>
                    CV (PDF/Word) <span className="text-red-600">*</span>
                  </span>
                }
                name="cvFile"
                // Accept both MIME types and extensions for broader browser support
                accept={[...ALLOWED_DOC_TYPES, ...ALLOWED_EXTS].join(",")}
                onChange={(e) => setCvFile(e.target.files?.[0] || null)}
              />
              {errors.cvFile ? <p className="mt-1 text-xs text-red-600">{errors.cvFile}</p> : null}
            </div>
          </div>

          {/* MTN Value LAST (force downward; safe if native select) */}
          <div className="mt-3 relative">
            <SelectField
              label="Values are the core of MTN Rwanda's culture. Our five values called ‘Live Y’ello’ is the force that unites and inspires each one of us to make an impact that matters in the world. So, tell us: Which one of our five Live Y’ello Values resonates most with you."
              name="mtnValue"
              options={[
                { value: "", label: "Select one" },
                { value: "Lead with Care", label: "Lead with Care" },
                { value: "Can-do with Integrity", label: "Can-do with Integrity" },
                { value: "Collaborate with Agility", label: "Collaborate with Agility" },
                { value: "Serve with Respect", label: "Serve with Respect" },
                { value: "Act with Inclusion", label: "Act with Inclusion" },
              ]}
              menuPlacement="bottom"
              menuPosition="fixed"
            />
            {errors.mtnValue ? (
              <p className="mt-1 text-xs text-red-600">{errors.mtnValue}</p>
            ) : null}
          </div>

          {/* Privacy Notice (MANDATORY) – with extra gap from MTN values */}
          <div className="flex items-start gap-2 pt-2 mt-2">
            <input
              id="privacy"
              type="checkbox"
              checked={!!consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
              required
            />
            <label htmlFor="privacy" className="text-sm text-gray-700">
              I have read and agree to the{" "}
              <a
                href="https://www.mtn.co.rw/privacy-notice-for-job-applicants/"
                className="text-blue-600 underline"
                target="_blank"
                rel="noreferrer"
              >
                Privacy notice
              </a>{" "}
              for job applicants
              {errors.privacy ? (
                <p className="mt-1 text-xs text-red-600">{errors.privacy}</p>
              ) : null}
            </label>
          </div>

          {/* Submit */}
          <div className="sticky bottom-0 py-2 bg-gray-50">
            <div className="flex justify-end max-w-3xl pt-2 mx-auto border-t">
              <Button
                label={loading ? "Submitting..." : "Submit Application"}
                disabled={loading}
                type="submit"
              />
            </div>
          </div>
        </form>
      </main>

      {/* === Education Modal (STACKED) === */}
      <Modal
        open={showEduModal}
        title={`Add Education Background ${educationList.length + 1}`}
        onClose={() => setShowEduModal(false)}
        footer={<Button label="Save" type="button" onClick={addEducation} />}
      >
        <div>
          <label className="block text-[0.9rem] font-medium">
            Institution <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name={`edu_institution_${educationList.length + 1}`}
            value={edu.institution}
            onChange={(e) => setEdu({ ...edu, institution: e.target.value })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            placeholder="e.g., University of Rwanda"
          />
        </div>

        <div>
          <label className="block text-[0.9rem] font-medium">
            Degree / Program <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name={`edu_degree_${educationList.length + 1}`}
            value={edu.degree}
            onChange={(e) => setEdu({ ...edu, degree: e.target.value })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            placeholder="e.g., BSc Computer Science"
          />
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div>
            <label className="block text-[0.9rem] font-medium">Start Date</label>
            <input
              type="date"
              name={`edu_start_${educationList.length + 1}`}
              value={edu.start}
              max={todayYMD()}
              onChange={(e) => setEdu({ ...edu, start: e.target.value })}
              className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            />
          </div>
          <div>
            <label className="block text-[0.9rem] font-medium">End Date</label>
            <input
              type="date"
              name={`edu_end_${educationList.length + 1}`}
              value={edu.end}
              disabled={edu.present}
              max={todayYMD()}
              onChange={(e) => setEdu({ ...edu, end: e.target.value })}
              className="w-full p-2 mt-1 border rounded-md disabled:bg-gray-100 hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            />
            <div className="mt-1 flex items-center space-x-2">
              <input
                id="edu_present"
                type="checkbox"
                checked={edu.present}
                onChange={(e) => setEdu({ ...edu, present: e.target.checked, end: "" })}
              />
              <label htmlFor="edu_present" className="text-sm">
                I’m currently studying here (Present)
              </label>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[0.9rem] font-medium">
            Document (PDF/Word) <span className="text-red-600">*</span>
          </label>
          <input
            type="file"
            accept={[...ALLOWED_DOC_TYPES, ...ALLOWED_EXTS].join(",")}
            onChange={(e) => setEdu({ ...edu, doc: e.target.files?.[0] || null })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
          />
        </div>
      </Modal>

      {/* === Work Modal (STACKED) === */}
      <Modal
        open={showWorkModal}
        title={`Add Work Experience ${workList.length + 1}`}
        onClose={() => setShowWorkModal(false)}
        footer={<Button label="Save" type="button" onClick={addWork} />}
      >
        <div>
          <label className="block text-[0.9rem] font-medium">
            Company <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name={`work_company_${workList.length + 1}`}
            value={work.company}
            onChange={(e) => setWork({ ...work, company: e.target.value })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            placeholder="e.g., MTN Rwanda"
          />
        </div>

        <div>
          <label className="block text-[0.9rem] font-medium">
            Role / Title <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name={`work_role_${workList.length + 1}`}
            value={work.role}
            onChange={(e) => setWork({ ...work, role: e.target.value })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            placeholder="e.g., Software Engineer"
          />
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div>
            <label className="block text-[0.9rem] font-medium">Start Date</label>
            <input
              type="date"
              name={`work_start_${workList.length + 1}`}
              value={work.start}
              max={maxDate}
              onChange={(e) => setWork({ ...work, start: e.target.value })}
              className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            />
          </div>
          <div>
            <label className="block text-[0.9rem] font-medium">End Date</label>
            <input
              type="date"
              name={`work_end_${workList.length + 1}`}
              value={work.end}
              disabled={work.present}
              max={maxDate}
              onChange={(e) => setWork({ ...work, end: e.target.value })}
              className="w-full p-2 mt-1 border rounded-md disabled:bg-gray-100 hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            />
            <div className="mt-1 flex items-center space-x-2">
              <input
                id="work_present"
                type="checkbox"
                checked={work.present}
                onChange={(e) => setWork({ ...work, present: e.target.checked, end: "" })}
              />
              <label htmlFor="work_present" className="text-sm">
                I still work here (Present)
              </label>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[0.9rem] font-medium">Document (PDF/Word)</label>
          <input
            type="file"
            accept={[...ALLOWED_DOC_TYPES, ...ALLOWED_EXTS].join(",")}
            onChange={(e) => setWork({ ...work, doc: e.target.files?.[0] || null })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
          />
        </div>
      </Modal>

      {/* === Certification Modal (STACKED) === */}
      <Modal
        open={showCertModal}
        title={`Add Certification ${certList.length + 1}`}
        onClose={() => setShowCertModal(false)}
        footer={<Button label="Save" type="button" onClick={addCert} />}
      >
        <div>
          <label className="block text-[0.9rem] font-medium">
            Certification Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name={`cert_name_${certList.length + 1}`}
            value={cert.name}
            onChange={(e) => setCert({ ...cert, name: e.target.value })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            placeholder="e.g., AWS Solutions Architect"
          />
        </div>

        <div>
          <label className="block text-[0.9rem] font-medium">
            Issuer / Authority <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            name={`cert_issuer_${certList.length + 1}`}
            value={cert.issuer}
            onChange={(e) => setCert({ ...cert, issuer: e.target.value })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            placeholder="e.g., Amazon"
          />
        </div>

        <div>
          <label className="block text-[0.9rem] font-medium">Year</label>
          <input
            type="text"
            name={`cert_year_${certList.length + 1}`}
            value={cert.year}
            onChange={(e) => setCert({ ...cert, year: e.target.value })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
            placeholder="YYYY"
          />
        </div>

        <div>
          <label className="block text-[0.9rem] font-medium">
            Document (PDF/Word) <span className="text-red-600">*</span>
          </label>
          <input
            type="file"
            accept={[...ALLOWED_DOC_TYPES, ...ALLOWED_EXTS].join(",")}
            onChange={(e) => setCert({ ...cert, doc: e.target.files?.[0] || null })}
            className="w-full p-2 mt-1 border rounded-md hover:border-[#FFCC00] focus:border-[#FFCC00] focus:shadow-[0_0_0_3px_rgba(255,204,0,0.35)]"
          />
        </div>
      </Modal>

      {/* Success dialog (centered, square, professional, no text about redirect) */}
      <SuccessDialog open={showSuccess} onClose={() => setShowSuccess(false)} />

      {/* Compact input + hover styles */}
      <style>{`
        [data-compact] input,
        [data-compact] select,
        [data-compact] textarea {
          padding: 0.35rem 0.5rem !important;
          font-size: 0.9rem !important;
          line-height: 1.2 !important;
        }
        [data-compact] label {
          font-size: 0.9rem !important;
        }
        [data-compact] input:hover,
        [data-compact] select:hover,
        [data-compact] textarea:hover {
          border-color: #FFCC00 !important;
          box-shadow: 0 0 0 2px rgba(255, 204, 0, 0.15);
        }
        [data-compact] input:focus,
        [data-compact] select:focus,
        [data-compact] textarea:focus,
        [data-compact] [contenteditable="true"]:focus {
          outline: none !important;
          border-color: #FFCC00 !important;
          --tw-ring-color: transparent !important;
          --tw-ring-offset-shadow: 0 0 #0000 !important;
          --tw-ring-shadow: 0 0 #0000 !important;
          box-shadow: 0 0 0 3px rgba(255, 204, 0, 0.35) !important;
        }

        /* Help dropdowns render above sticky/footer if using portals */
        .Select__menu-portal, .select-menu-portal {
          z-index: 70;
        }
      `}</style>
    </div>
  );
};

export default ApplicationForm;
